import React, { useMemo } from 'react'
import "./ReviewMessageEdit.scss";
import EditFormActionsFooter from './EditFormActionsFooter';
import { Divider } from '@livingdesign/react';
import { useMessageDetailsForm } from '../../../../../contexts/MessageDetailsForm';
import UploadedAttachmentListItem from './FormComponents/UploadedAttachmentListItem';
import moment from 'moment';
import SendTestMessageActionBanner from './FormComponents/SendTestMessageActionBanner';
import DOMPurify from 'dompurify';
import { useCampaignContext } from '../../../../../contexts/Campaign';
import { CAMPAIGN_EDIT_STEPS, STEPS_V2 } from '../../../../../consts/CampaignEdit';
import reviewErrorIcon from '../../../../../assets/svg/message-review-icon.svg'
import { isSystemNotificationsCampaign } from '../../../../../utils/campaign';
import { CAMPAIGN } from '../../../../../consts/DBFields';
import { useHistory } from 'react-router-dom';

export const QA_ID_PREFIX = "Campaign-Edit-ReviewMessage";

function ReviewMessageEdit() {

  const {
    templatePreview,
  } = useCampaignContext();

  const {
    formValues,
    continueButtonLoading,
    setContinueButtonLoading,
    saveCampaign,
    selectedCampaign,
    channel,
    setCurrentStep,
    globalErrorMessage,
    handlePrevious,
    handleSchedule,
    handleSend,
    saveDraft,
  } = useMessageDetailsForm({});

  const history = useHistory();

  const getFormattedDate = (date) => {
    if(!date) return "N/A";
    return moment(date).format("MM/DD/yyyy")
  }

  const getFormattedTime = (time) => {
    if(!time) return "N/A";
    return moment(time, "HH:mm").format("hh:mm A")
  }

  // Navigate to a specific step in the campaign edit flow
  const goToStep = (stepName) => {
    const stepIndex = CAMPAIGN_EDIT_STEPS[channel].findIndex(step => step.label === stepName);
    if (stepIndex !== -1) {
      setCurrentStep(stepIndex);
    }
  }

  const continueButtonLabel = useMemo(() => {
    if(selectedCampaign[CAMPAIGN.IS_SYSTEM_NOTIFICATIONS]) return "Complete draft";
    return formValues.deliveryType === "send-now" ? "Send message" : "Schedule message";
  }, [formValues.deliveryType, selectedCampaign[CAMPAIGN.IS_SYSTEM_NOTIFICATIONS]]);

  // Handle the continue button click
  const handleContinue = async () => {
    if(selectedCampaign[CAMPAIGN.IS_SYSTEM_NOTIFICATIONS]) {
      setContinueButtonLoading(true);
      await saveCampaign(selectedCampaign);
      history.push({
        pathname: `/folders/${selectedCampaign[CAMPAIGN.FOLDER_ID]}`,
      });
      setContinueButtonLoading(false);
    } else if(formValues.deliveryType === "send-now") {
      await handleSend();
    } else {
      await handleSchedule();
    }
  };

  return (
    <div data-testid={`${QA_ID_PREFIX}-Container`}>
      <div className='review-message-details-title' data-testid={`${QA_ID_PREFIX}-Title-Container`}>
        <h2 data-testid={`${QA_ID_PREFIX}-Heading`}>Review your message</h2>
        <p data-testid={`${QA_ID_PREFIX}-Description`}>Review the drafted details and templates to ensure accuracy and make any necessary adjustments before finalizing.</p>
      </div>
      {globalErrorMessage &&
        <div className='review-message-error' data-testid={`${QA_ID_PREFIX}-Error-Container`}>
          <img src={reviewErrorIcon} data-testid={`${QA_ID_PREFIX}-Error-Icon`}/>
          <h3 data-testid={`${QA_ID_PREFIX}-Error-Heading`}>Message preview isn't available right now</h3>
          <div data-testid={`${QA_ID_PREFIX}-Error-Message`}>Please complete all the required fields to generate the message preview.</div>
        </div>
      }
      {!globalErrorMessage &&
        <div className='review-message-details-container' data-testid={`${QA_ID_PREFIX}-Details-Container`}>
        {channel === "mail" &&
        <>
          <div className='mb-8px mt-24px' style={{display: "flex", justifyContent: "space-between"}} data-testid={`${QA_ID_PREFIX}-Message-Details-Header`}>
            <h3 data-testid={`${QA_ID_PREFIX}-Message-Details-Title`}>Message details</h3>
            <button
              className='message-review-edit-button'
              onClick={() => goToStep(STEPS_V2.DETAILS.label)}
              data-testid={`${QA_ID_PREFIX}-Message-Details-Edit-Button`}
            >
              Edit
            </button>
          </div>

          <div className='mb-8px' data-testid={`${QA_ID_PREFIX}-From-Container`}>
            <h4 data-testid={`${QA_ID_PREFIX}-From-Label`}>From</h4>
            <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-From-Value`}>{formValues.from}</div>
          </div>
        
          <div className='flex-row' data-testid={`${QA_ID_PREFIX}-Priority-Subject-Container`}>
            <div data-testid={`${QA_ID_PREFIX}-Priority-Container`}>
              <h4 data-testid={`${QA_ID_PREFIX}-Priority-Label`}>Priority</h4>
              <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Priority-Value`}>{formValues.priority}</div>
            </div>
            <div style={{minWidth: '400px'}} data-testid={`${QA_ID_PREFIX}-Subject-Container`}>
              <h4 data-testid={`${QA_ID_PREFIX}-Subject-Label`}>Subject</h4>
              <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Subject-Value`}>{formValues.subject}</div>
            </div>
          </div>

          <div className='mb-8px' data-testid={`${QA_ID_PREFIX}-Snippet-Container`}>
            <h4 data-testid={`${QA_ID_PREFIX}-Snippet-Label`}>Snippet</h4>
            <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Snippet-Value`}>
            {formValues.snippet || "N/A"}
            </div>
          </div>
        
          <div className='mb-8px' data-testid={`${QA_ID_PREFIX}-Personalisation-Container`}>
            <h4 data-testid={`${QA_ID_PREFIX}-Personalisation-Label`}>Message personalisation</h4>
            <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Personalisation-Value`}>{formValues.personaliseMessage ? "On" : "Off"}</div>
          </div>

          <div className='flex-row mb-8px' data-testid={`${QA_ID_PREFIX}-Tracking-Container`}>
            <div data-testid={`${QA_ID_PREFIX}-Track-Opens-Container`}>
              <h4 data-testid={`${QA_ID_PREFIX}-Track-Opens-Label`}>Track opens</h4>
              <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Track-Opens-Value`}>{formValues.trackOpens ? "On" : "Off"}</div>
            </div>
            <div data-testid={`${QA_ID_PREFIX}-Track-Clicks-Container`}>
              <h4 data-testid={`${QA_ID_PREFIX}-Track-Clicks-Label`}>Track clicks</h4>
              <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Track-Clicks-Value`}>{formValues.trackClicks ? "On" : "Off"}</div>
            </div>
            <div data-testid={`${QA_ID_PREFIX}-Advanced-Tracking-Container`}>
              <h4 data-testid={`${QA_ID_PREFIX}-Advanced-Tracking-Label`}>Advanced tracking</h4>
              <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Advanced-Tracking-Value`}>{formValues.advancedTracking ? "On" : "Off"}</div>
            </div>
          </div>
          <Divider UNSAFE_style={{ marginTop: "24px"}} data-testid={`${QA_ID_PREFIX}-Message-Details-Divider`} />
        </>
        }

        {!isSystemNotificationsCampaign(selectedCampaign) && 
          <>
            <div className='mb-8px mt-24px' style={{display: "flex", justifyContent: "space-between"}} data-testid={`${QA_ID_PREFIX}-Recipients-Header`}>
              <h3 data-testid={`${QA_ID_PREFIX}-Recipients-Title`}>Recipients</h3>
              <button
                className='message-review-edit-button'
                onClick={() => goToStep(STEPS_V2.RECIPIENTS.label)}
                data-testid={`${QA_ID_PREFIX}-Recipients-Edit-Button`}
              >
                Edit
              </button>
            </div>
            <div className='flex-row mb-8px' data-testid={`${QA_ID_PREFIX}-Recipients-Details-Container`}>
              <div data-testid={`${QA_ID_PREFIX}-To-Container`}>
                <h4 data-testid={`${QA_ID_PREFIX}-To-Label`}>To</h4>
                <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-To-Value`}>#Recipients</div>
              </div>
              {channel === "mail" && 
              <>
                <div data-testid={`${QA_ID_PREFIX}-Cc-Container`}>
                  <h4 data-testid={`${QA_ID_PREFIX}-Cc-Label`}>Cc</h4>
                  <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Cc-Value`}>#Recipients</div>
                </div>
                <div data-testid={`${QA_ID_PREFIX}-Bcc-Container`}>
                  <h4 data-testid={`${QA_ID_PREFIX}-Bcc-Label`}>Bcc</h4>
                  <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Bcc-Value`}>#Recipients</div>
                </div>
              </>
              }
            </div>
            <Divider UNSAFE_style={{marginBottom: "24px", marginTop: "24px"}} data-testid={`${QA_ID_PREFIX}-Recipients-Divider`} />
          </>
        }
        
        {channel === "mail" &&
          <div className='mb-8px' data-testid={`${QA_ID_PREFIX}-Content-Container`}>
            <div className='mb-8px mt-24px' style={{display: "flex", justifyContent: "space-between"}} data-testid={`${QA_ID_PREFIX}-Content-Header`}>
              <h3 data-testid={`${QA_ID_PREFIX}-Content-Title`}>Content</h3>
              <button
                className='message-review-edit-button'
                onClick={() => goToStep(STEPS_V2.CONTENT.label)}
                data-testid={`${QA_ID_PREFIX}-Content-Edit-Button`}
              >
                Edit
              </button>
            </div>
            <h4 data-testid={`${QA_ID_PREFIX}-Template-Label`}>Template</h4>
            <div style={{marginTop: "8px"}} data-testid={`${QA_ID_PREFIX}-Template-Container`}>
              <div className='html-template-preview-overlay' data-testid={`${QA_ID_PREFIX}-Template-Overlay`} />
              <div
                className='html-template-preview'
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(templatePreview) }}
                data-testid={`${QA_ID_PREFIX}-Template-Preview`}
              />
            </div>
            <div className='mt-8px' data-testid={`${QA_ID_PREFIX}-Attachments-Container`}>
              <h4 data-testid={`${QA_ID_PREFIX}-Attachments-Label`}>Attachments</h4>
              <div className='line-height-24px mt-8px' data-testid={`${QA_ID_PREFIX}-Attachments-Value`}>
                {(formValues.attachments && formValues.attachments.length > 0)
                ?
                  formValues.attachments.map((attachment, index) => (
                    <UploadedAttachmentListItem
                      key={`${attachment.filename}-${index}`}
                      index={index}
                      fileName={attachment.filename}
                      removable={false}
                    />
                  ))
                :
                "N/A"
                }
              </div>
            </div>
          </div>
        }
        {channel === "teams" &&
          <div className='mb-8px' data-testid={`${QA_ID_PREFIX}-Teams-Content-Container`}>
            <div className='mb-8px mt-24px' style={{display: "flex", justifyContent: "space-between"}} data-testid={`${QA_ID_PREFIX}-Teams-Content-Header`}>
              <h3 className='mb-8px' data-testid={`${QA_ID_PREFIX}-Teams-Content-Title`}>Build notifications</h3>
              <button
                className='message-review-edit-button'
                onClick={() => goToStep(STEPS_V2.BUILD_TEAMS_NOTIFICATIONS.label)}
                data-testid={`${QA_ID_PREFIX}-Teams-Content-Edit-Button`}
              >
                Edit
              </button>
            </div>
            <h4 className='mb-8px' data-testid={`${QA_ID_PREFIX}-Bot-Code-Label`}>Bot code</h4>
            <div className='bot-code-preview' data-testid={`${QA_ID_PREFIX}-Bot-Code-Preview`}>{formValues.templateContent}</div>
          </div>
        }
        <Divider UNSAFE_style={{marginBottom: "24px", marginTop: "24px"}} data-testid={`${QA_ID_PREFIX}-Content-Divider`} />

        {!isSystemNotificationsCampaign(selectedCampaign) && 
          <>
            <div className='mb-8px mt-24px' style={{display: "flex", justifyContent: "space-between"}} data-testid={`${QA_ID_PREFIX}-Delivery-Header`}>
              <h3 data-testid={`${QA_ID_PREFIX}-Delivery-Title`}>Delivery time</h3>
              <button
                className='message-review-edit-button'
                onClick={() => goToStep(STEPS_V2.SCHEDULE.label)}
                data-testid={`${QA_ID_PREFIX}-Delivery-Edit-Button`}
              >
                Edit
              </button>
            </div>
            <div className='flex-row' data-testid={`${QA_ID_PREFIX}-Delivery-Details-Container`}>
              <div data-testid={`${QA_ID_PREFIX}-Delivery-Type-Container`}>
                <h4 data-testid={`${QA_ID_PREFIX}-Delivery-Type-Label`}>Delivery type</h4>
                <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Delivery-Type-Value`}>{(formValues.deliveryType === "send-now") ? "Send now" : "Schedule for later"}</div>
              </div>
              <div data-testid={`${QA_ID_PREFIX}-Delivery-Date-Container`}>
                <h4 data-testid={`${QA_ID_PREFIX}-Delivery-Date-Label`}>Date</h4>
                <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Delivery-Date-Value`}>{(formValues.deliveryType === "send-now") ? "N/A" : getFormattedDate(formValues.scheduledDate)}</div>
              </div>
              <div data-testid={`${QA_ID_PREFIX}-Delivery-Time-Container`}>
                <h4 data-testid={`${QA_ID_PREFIX}-Delivery-Time-Label`}>Time</h4>
                <div className='line-height-24px' data-testid={`${QA_ID_PREFIX}-Delivery-Time-Value`}>{(formValues.deliveryType === "send-now") ? "N/A" : getFormattedTime(formValues.scheduledTime)}</div>
              </div>
            </div>
            <Divider UNSAFE_style={{marginBottom: "24px", marginTop: "24px"}} data-testid={`${QA_ID_PREFIX}-Delivery-Divider`} />
          </>
        }

        <SendTestMessageActionBanner />
        </div>
      }
      <EditFormActionsFooter
        loading={continueButtonLoading}
        onPrevious={() => handlePrevious()}
        onSaveDraft={() => saveDraft()}
        disableContinue={globalErrorMessage}
        onContinue={handleContinue}
        continueLabel={continueButtonLabel}
      />
    </div>
  )
}

export default ReviewMessageEdit;